import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Snackbar,
  FormHelperText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import { organizationService } from '../../services/organizationService';
import { vehicleService } from '../../services/vehicleService';
import { OrganizationMember, Role, OrganizationInvitation } from '../../types';
import { Vehicle } from '../../types/vehicle';
import { useAuth } from '../../contexts/AuthContext';

interface ManageMembersProps {
  organizationId: string;
}

interface AddMemberDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  onInvite: (email: string, role: Role, functionTitle: string, vehicleId?: string) => Promise<void>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AddMemberDialog = ({ open, onClose, organizationId, onInvite }: AddMemberDialogProps) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('User');
  const [functionTitle, setFunctionTitle] = useState('');
  const [vehicleId, setVehicleId] = useState<string>('');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  useEffect(() => {
    const loadVehicles = async () => {
      if (!open || role !== 'User') return;
      
      try {
        setLoadingVehicles(true);
        console.log('Loading unassigned vehicles for organization:', organizationId);
        const vehiclesList = await vehicleService.getUnassignedVehicles(organizationId);
        console.log('Loaded vehicles:', vehiclesList);
        setVehicles(vehiclesList);
      } catch (err) {
        console.error('Error loading vehicles:', err);
        setError('Failed to load available vehicles');
      } finally {
        setLoadingVehicles(false);
      }
    };

    loadVehicles();
  }, [organizationId, open, role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !functionTitle.trim()) {
      setError('All fields are required');
      return;
    }

    if (role === 'User' && !vehicleId) {
      setError('Please select a vehicle for the driver');
      return;
    }

    try {
      setError('');
      setLoading(true);
      console.log('Submitting invitation with data:', {
        email: email.trim(),
        role,
        functionTitle: functionTitle.trim(),
        vehicleId: role === 'User' ? vehicleId : undefined
      });

      // Vérifier que le véhicule existe toujours
      if (role === 'User' && vehicleId) {
        const vehicle = await vehicleService.getVehicle(vehicleId);
        if (!vehicle) {
          throw new Error('Selected vehicle not found');
        }
        console.log('Vehicle verified:', vehicle);
      }

      await onInvite(email.trim(), role, functionTitle.trim(), role === 'User' ? vehicleId : undefined);
      setEmail('');
      setRole('User');
      setFunctionTitle('');
      onClose();
    } catch (err) {
      console.error('Error inviting member:', err);
      setError(err instanceof Error ? err.message : 'Failed to invite member');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole('User');
    setFunctionTitle('');
    setVehicleId('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite New Member</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!error && !email.trim()}
            helperText={error && !email.trim() ? 'Email is required' : ''}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              value={role}
              label="Role"
              onChange={(e) => setRole(e.target.value as Role)}
            >
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="User">User</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Function/Title"
            type="text"
            fullWidth
            required
            value={functionTitle}
            onChange={(e) => setFunctionTitle(e.target.value)}
            error={!!error && !functionTitle.trim()}
            helperText={error && !functionTitle.trim() ? 'Function is required' : ''}
          />
          
          {role === 'User' && (
            <FormControl 
              fullWidth 
              margin="dense"
              error={!!error && !vehicleId}
            >
              <InputLabel>Assigned Vehicle</InputLabel>
              <Select
                value={vehicleId}
                label="Assigned Vehicle"
                onChange={(e) => setVehicleId(e.target.value)}
                disabled={loadingVehicles}
              >
                {loadingVehicles ? (
                  <MenuItem value="">
                    <CircularProgress size={20} /> Loading vehicles...
                  </MenuItem>
                ) : vehicles.length === 0 ? (
                  <MenuItem value="">No vehicles available</MenuItem>
                ) : (
                  vehicles.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.make} {vehicle.model} ({vehicle.licensePlate})
                    </MenuItem>
                  ))
                )}
              </Select>
              {error && !vehicleId && (
                <FormHelperText>Please select a vehicle</FormHelperText>
              )}
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !email.trim() || !functionTitle.trim()}
          >
            {loading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const ManageMembers = ({ organizationId }: ManageMembersProps) => {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<OrganizationInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const { user } = useAuth();

  const loadData = async () => {
    try {
      setLoading(true);
      const [membersList, invitationsList] = await Promise.all([
        organizationService.getOrganizationMembers(organizationId),
        organizationService.getPendingInvitations(organizationId),
      ]);
      setMembers(membersList);
      setPendingInvitations(invitationsList);
      setError('');
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [organizationId]);

  const handleInviteMember = async (email: string, role: Role, functionTitle: string, vehicleId?: string) => {
    try {
      console.log('Inviting member with data:', { email, role, functionTitle, vehicleId });
      
      // Si c'est un conducteur, vérifier que le véhicule existe
      if (role === 'User' && vehicleId) {
        const vehicle = await vehicleService.getVehicle(vehicleId);
        if (!vehicle) {
          throw new Error('Selected vehicle not found');
        }
        console.log('Vehicle verified:', vehicle);
      }
      
      await organizationService.inviteMember(organizationId, email, role, functionTitle, vehicleId);
      await loadData();
      setSnackbar({
        open: true,
        message: 'Invitation sent successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error inviting member:', err);
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to send invitation',
        severity: 'error',
      });
      throw err;
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: Role) => {
    try {
      await organizationService.updateMemberRole(organizationId, memberId, newRole);
      await loadData();
      setSnackbar({
        open: true,
        message: 'Role updated successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error updating role:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update member role',
        severity: 'error',
      });
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (window.confirm('Are you sure you want to remove this member?')) {
      try {
        await organizationService.removeMember(organizationId, memberId);
        await loadData();
        setSnackbar({
          open: true,
          message: 'Member removed successfully',
          severity: 'success',
        });
      } catch (err) {
        console.error('Error removing member:', err);
        setSnackbar({
          open: true,
          message: 'Failed to remove member',
          severity: 'error',
        });
      }
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await organizationService.resendInvitation(organizationId, invitationId);
      setSnackbar({
        open: true,
        message: 'Invitation resent successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error resending invitation:', err);
      setSnackbar({
        open: true,
        message: 'Failed to resend invitation',
        severity: 'error',
      });
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (window.confirm('Are you sure you want to cancel this invitation?')) {
      try {
        await organizationService.cancelInvitation(organizationId, invitationId);
        await loadData();
        setSnackbar({
          open: true,
          message: 'Invitation cancelled successfully',
          severity: 'success',
        });
      } catch (err) {
        console.error('Error canceling invitation:', err);
        setSnackbar({
          open: true,
          message: 'Failed to cancel invitation',
          severity: 'error',
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={loadData}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <div>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Members" />
          <Tab label={`Invitations (${pendingInvitations.length})`} />
        </Tabs>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpenAddDialog(true)}
        sx={{ mb: 2 }}
      >
        Invite Member
      </Button>

      <TabPanel value={tabValue} index={0}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Function</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.userId}>
                  <TableCell>
                    <Box component="span">
                      {member.displayName}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box component="span">{member.email}</Box>
                  </TableCell>
                  <TableCell>
                    <Box component="span">{member.functionTitle}</Box>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small">
                      <Select
                        value={member.role}
                        onChange={(e) => handleUpdateRole(member.userId, e.target.value as Role)}
                        disabled={member.userId === user?.id}
                      >
                        <MenuItem value="Admin">Admin</MenuItem>
                        <MenuItem value="User">User</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    {member.userId !== user?.id && (
                      <IconButton
                        onClick={() => handleRemoveMember(member.userId)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {members.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ textAlign: 'center' }}>
                      No members found
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Function</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingInvitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell>
                    <Box component="span">{invitation.email}</Box>
                  </TableCell>
                  <TableCell>
                    <Box component="span">{invitation.functionTitle}</Box>
                  </TableCell>
                  <TableCell>
                    <Box component="span">{invitation.role}</Box>
                  </TableCell>
                  <TableCell>
                    <Box component="span">
                      {invitation.createdAt?.toDate().toLocaleDateString()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleResendInvitation(invitation.id)}
                      color="primary"
                      title="Resend invitation"
                      sx={{ mr: 1 }}
                    >
                      <ReplayIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleCancelInvitation(invitation.id)}
                      color="error"
                      title="Cancel invitation"
                    >
                      <CancelIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {pendingInvitations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ textAlign: 'center' }}>
                      No pending invitations
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <AddMemberDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onInvite={handleInviteMember}
        organizationId={organizationId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ManageMembers;
